import {useCallback} from 'react';
import {makeVar, useApolloClient, useReactiveVar} from '@apollo/client';
import {useRouter} from 'next/router';

import {useCreateAuthenticationTokenMutation} from '#apollo/mutation/useCreateAuthenticationToken';
import {CreateUserAccountInput, useCreateUserAccountMutation} from '#apollo/schema';
import {useRequestPasswordReset} from '#apollo/schema';
import {useResetPassword} from '#apollo/schema';
import {useAnalytics} from '#component/provider/AnalyticsProvider';
import {EventType} from '#component/provider/AnalyticsProvider';
import {UserSignedOutEvent} from '#component/provider/AnalyticsProvider';
import {UserIdentifiedEvent} from '#component/provider/AnalyticsProvider';
import {useFirebase} from '#hook/useFirebase';
import {PersistentTokenName, usePersistentToken} from '#hook/usePersistentToken';
import {handleRedirectToUrl} from '#hook/useReturnUrl';
import * as DESTINATION from '#hook/useReturnUrl/defaultDestination';
import {buildUrl} from '#util/url';

import {SignInOptions} from './useAuth.types';
import {SignOutOptions} from './useAuth.types';
import {SignUpOptions} from './useAuth.types';

const signedInVar = makeVar(false);

/**
 * The `useAuth` hook provides implementation-independent interface to various
 * authentication related functions, as well as performing side effects related
 * to authentication.
 */
export const useAuth = () => {
  const signedIn = useReactiveVar(signedInVar);
  const router = useRouter();
  const {track} = useAnalytics();
  const apolloClient = useApolloClient();
  const [createUserAccount, {loading: createAccountLoading}] = useCreateUserAccountMutation();
  const {createAuthenticationToken, loading: createAuthenticationTokenLoading} =
    useCreateAuthenticationTokenMutation();
  const [requestResetPassword] = useRequestPasswordReset();
  const [resetPassword] = useResetPassword();

  const {setToken: setAuthenticationToken, removeToken: removeAuthenticationToken} =
    usePersistentToken(PersistentTokenName.AuthenticationToken);

  const {setToken: setUserIdToken, removeToken: removeUserIdToken} = usePersistentToken(
    PersistentTokenName.UserIdToken,
  );

  const {
    signIn: signInWithFirebase,
    signOut: signOutWithFirebase,
    verifyCredential: verifyCredentialWithFirebase,
    getCurrentUser: getCurrentUserWithFirebase,
  } = useFirebase();

  const clearUserSessionData = useCallback(async () => {
    await removeUserIdToken();
    await removeAuthenticationToken();
    await signOutWithFirebase();
    await apolloClient.clearStore();
    signedInVar(false);
  }, [apolloClient, removeUserIdToken, removeAuthenticationToken, signOutWithFirebase]);

  const handleIsUserSignedIn = useCallback(
    <T extends boolean | undefined = false>({async = false}: {async?: T} = {}): T extends true
      ? Promise<boolean>
      : boolean => {
      // If we're running in NextJS's server side code ...
      if (typeof window === 'undefined') {
        // ... then don't bother checking Firebase 'cause it's not setup to run
        // on server side (it would result in an unhandled exception if we do).
        return async ? Promise.resolve(false) : (false as any);
      }
      // If we're running on client side ...
      else {
        // ... run a check to see if authentication state has changed
        const promise = getCurrentUserWithFirebase()
          .then(user => {
            const result = !!user;
            signedInVar(result);
            return result;
          })
          .catch(() => {
            signedInVar(false);
            return false;
          });

        // ... meanwhile, return either the promise or the current state value.
        return async ? promise : (signedIn as any);
      }
    },
    [getCurrentUserWithFirebase, signedIn],
  );

  const handleSignUp = async (input: CreateUserAccountInput, opts?: SignUpOptions) => {
    const response = await createUserAccount({variables: {input}});
    const {data} = response;

    if (data?.createUserAccount.userId) {
      const {userId, authToken} = data?.createUserAccount;

      await clearUserSessionData();
      await signInWithFirebase(input.email, input.password, true);
      await setAuthenticationToken(authToken);
      await setUserIdToken(userId);

      const firebaseUser = await getCurrentUserWithFirebase();

      signedInVar(!!firebaseUser);
      track<UserIdentifiedEvent>({evtName: EventType.UserIdentified, userId});
    }

    if (opts?.redirect) {
      await handleRedirectToUrl(router, opts.redirect, DESTINATION.HOME);
    } else {
      await handleRedirectToUrl(router, true, DESTINATION.HOME);
    }
  };

  const handleSignIn = async (
    {email, password}: {email: string; password: string},
    opts?: SignInOptions,
  ) => {
    const createAuthenticationTokenResult = await createAuthenticationToken({email, password});

    if (createAuthenticationTokenResult) {
      const {userId, token} = createAuthenticationTokenResult;

      await clearUserSessionData();
      await signInWithFirebase(email, password, opts?.rememberAuth ?? true);
      await setAuthenticationToken(token);
      await setUserIdToken(userId);

      const firebaseUser = await getCurrentUserWithFirebase();

      signedInVar(!!firebaseUser);
      track<UserIdentifiedEvent>({evtName: EventType.UserIdentified, userId});
    }

    if (opts?.redirect) {
      await handleRedirectToUrl(router, opts.redirect, DESTINATION.HOME);
    } else {
      await handleRedirectToUrl(router, true, DESTINATION.HOME);
    }
  };

  const handleSignOut = useCallback(
    async (opts?: SignOutOptions) => {
      track<UserSignedOutEvent>({evtName: EventType.UserSignedOut});
      await clearUserSessionData();

      if (opts?.redirect) {
        await handleRedirectToUrl(
          router,
          opts.redirect,
          // Signing out has no default redirect destination because the sign-out
          // page renders a "signed out" view.
          null,
          {
            // When signing out, we should always force a page load (instead of
            // using `router`). This way we can get rid of apollo's cache as well.
            forcePageLoad: true,
          },
        );
      }
    },
    [router, track, clearUserSessionData],
  );

  const handleVerifyCredential = async ({email, password}: {email: string; password: string}) => {
    return await verifyCredentialWithFirebase(email, password);
  };

  const handleForgotPassword = async ({
    email,
    returnUrl,
  }: {
    email: string;
    returnUrl?: string;
  }): Promise<boolean> => {
    const {data} = await requestResetPassword({variables: {input: {email, returnUrl: returnUrl}}});

    await clearUserSessionData();

    return !!data?.requestPasswordReset.success;
  };

  const handleResetPassword = async ({
    newPassword,
    token,
    returnUrl,
    resolveTarget,
  }: {
    newPassword: string;
    token: string;
    returnUrl?: string;
    resolveTarget?: (defaultTarget: string | null) => Promise<string | null>;
  }): Promise<boolean> => {
    const wasSignedIn = handleIsUserSignedIn();

    const {data} = await resetPassword({
      variables: {input: {newPassword, token}},
    });

    await clearUserSessionData();

    if (returnUrl && resolveTarget) {
      await handleRedirectToUrl(router, true, async () => {
        return await resolveTarget(
          buildUrl({
            path: DESTINATION.RESET_PASSWORD({wasSignedIn}),
            queries: {return_to: returnUrl},
          }),
        );
      });
    }

    return !!data?.resetPassword.success;
  };

  return {
    isUserSignedIn: handleIsUserSignedIn,
    signUp: handleSignUp,
    signUpLoading: createAccountLoading,
    signIn: handleSignIn,
    signInLoading: createAuthenticationTokenLoading,
    signOut: handleSignOut,
    verifyCredential: handleVerifyCredential,
    forgotPassword: handleForgotPassword,
    resetPassword: handleResetPassword,
  };
};
